import { create } from "zustand";

import { apiV2, selectUser } from "api/firebase";
import { devtools } from "zustand/middleware";
import { IGetOneCurrentExhibitorRes } from "@evennt-tech/api-v2-external";
import {
  ExhibitorUserConnectionType,
  IExhibitorPair,
  IExhibitorUserConnection,
  INotificationBaseRelatedToShow,
  NotificationType,
  Roles,
} from "@evennt-tech/api-v2-core";
import { IdleManager } from "tools/idle";
import { registerStore } from "store/utils/reset-store.utils";

/**
 * `UPDATED_SHOW` covers a language the organiser or the Admin disabled on the
 * Show: the payload is localised server-side, so it must be refetched to fall
 * back to the Show's default language (FS-3075).
 */
const REFETCH_ON: NotificationType[] = [
  NotificationType.UPDATED_BUILD_TYPE,
  NotificationType.UPDATED_SHOW,
];

type State = {
  item: IGetOneCurrentExhibitorRes | null;
  isLoading: boolean;
};

type Action = {
  fetch: (force?: boolean) => Promise<void>;
  update: () => Promise<void>;
  setSelectedLanguage: (selectedLanguage: string) => Promise<void>;
  autoCatch: (showId?: string) => () => void;
  getAll: () => IGetOneCurrentExhibitorRes | null;
  getExhibitorPair: (
    showId: string,
    exhibitorPairId: string
  ) => {
    pair: IExhibitorPair | null;
    connection: IExhibitorUserConnection | null;
  };
  isPrimaryUserByStand(showId: string, exhibitorPairId: string): boolean;
};

export const useCurrentExhibitor = create(
  devtools<State & Action>((set, get) => ({
    item: null,
    isLoading: false,

    getAll: () => {
      const { item } = get();

      return item;
    },
    isPrimaryUserByStand: (
      showId: string,
      exhibitorPairId: string
    ): boolean => {
      const { item } = get();

      if (!item) {
        return false;
      }

      return item.connections.some(
        (connection) =>
          connection.exhibitorPairId === exhibitorPairId &&
          connection.showId === showId &&
          connection.role === ExhibitorUserConnectionType.PRIMARY
      );
    },
    getExhibitorPair: (showId: string, exhibitorPairId: string) => {
      const { item } = get();

      if (!item) {
        return {
          pair: null,
          connection: null,
        };
      }

      const connectionWithPair = item.connections.find(
        (connection) =>
          connection.exhibitorPairId === exhibitorPairId &&
          connection.showId === showId
      );

      if (!connectionWithPair) {
        return {
          pair: null,
          connection: null,
        };
      }

      return {
        pair: connectionWithPair.exhibitorPair,
        connection: connectionWithPair,
      };
    },

    autoCatch: (showId?: string) => {
      const { fetch } = get();

      if (!showId) {
        return () => null;
      }

      const listenerId = "current_exhibitor_store";

      IdleManager.getInstance().addSubscriber(
        `USE_CURRENT_EXHIBITOR-${showId}`,
        async () => {
          await fetch(true);
        }
      );

      REFETCH_ON.forEach((key) => {
        apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
          key,
          showId,
          listenerId,
          callback: async () => {
            if (get().item) {
              await get().update();
            }
          },
        });
      });

      return () =>
        REFETCH_ON.forEach((key) =>
          apiV2.notification.unsubscribe({
            key,
            showId,
            listenerId,
          })
        );
    },

    setSelectedLanguage: async (selectedLanguage: string) => {
      await apiV2.exhibitor.current.updateSelectedLanguage({
        selectedLanguage,
      });
      await get().fetch(true);
    },

    update: async () => {
      const user = selectUser();

      if (!user) {
        return;
      }

      const { role } = await apiV2.user.getMe();

      if (role !== Roles.EXHIBITOR) return;

      const item = await apiV2.exhibitor.current.get().catch(() => null);

      set(() => ({
        item,
      }));
    },
    fetch: async (force = false) => {
      const { update } = get();

      const exhibitorId = selectUser()?.id;

      if (!force) {
        if (get()?.item?.user?.id === exhibitorId || get().isLoading) {
          return;
        }
      }

      if (!get().item) {
        set(() => ({ isLoading: true }));
      }

      try {
        await update();
      } catch (e) {
        console.error(e);
      } finally {
        set(() => ({ isLoading: false }));
      }
    },
  }))
);

registerStore(useCurrentExhibitor);
